/**
 * Safety report API contract placeholder.
 * 
 * ACT 8: Frontend API contract for sponsor safety/fraud/abuse reports.
 * This is a minimal, safety-first implementation.
 * 
 * Safety doctrine:
 * - No personal identity required in report
 * - No contact details (email, phone, address)
 * - No legal name, government ID, employer
 * - No social media handles or financial data
 * - Reports are for safety context only
 * 
 * TODO(ACT 8): Replace with real API calls when backend is ready.
 * TODO(ACT 8): Add real audit event emission for report_submitted.
 */

/**
 * Report types for safety/fraud/abuse concerns.
 */
export type ReportType =
  | 'safety_concern'
  | 'suspected_fraud'
  | 'inappropriate_content'
  | 'privacy_concern'
  | 'technical_issue'
  | 'other';

/**
 * Report draft - data sponsor submits.
 * No forbidden identity/contact/financial fields.
 */
export interface ReportDraft {
  /** Type of report */
  reportType: ReportType;
  /** Optional related card ID for context */
  relatedCardId?: string;
  /** Optional description, max 500 chars */
  description?: string;
  /** Sponsor acknowledged no unnecessary personal data */
  acknowledged: boolean;
}

/**
 * Report status.
 */
export type ReportStatus = 'received' | 'reviewing' | 'resolved';

/**
 * Report response - confirmation of submission.
 */
export interface ReportResponse {
  /** Unique report ID */
  reportId: string;
  /** Current status */
  status: ReportStatus;
  /** Timestamp of submission */
  submittedAt: string;
}

/**
 * API contract for report operations.
 * Currently uses local state; will call real API in future ACTs.
 */
export interface ReportsAPI {
  /**
   * Submit a safety report.
   * 
   * TODO(ACT 8): Replace with: POST /api/reports
   * TODO(ACT 8): Emit report_submitted audit event
   */
  submitReport: (draft: ReportDraft) => Promise<ReportResponse>;
}

/**
 * Report type labels for UI display.
 */
export const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  safety_concern: 'Safety Concern',
  suspected_fraud: 'Suspected Fraud',
  inappropriate_content: 'Inappropriate Content',
  privacy_concern: 'Privacy Concern',
  technical_issue: 'Technical Issue',
  other: 'Other',
};

/**
 * Report type descriptions for UI.
 */
export const REPORT_TYPE_DESCRIPTIONS: Record<ReportType, string> = {
  safety_concern: 'Report a safety issue or threat',
  suspected_fraud: 'Report suspected fraudulent activity',
  inappropriate_content: 'Report inappropriate content or behavior',
  privacy_concern: 'Report a privacy violation',
  technical_issue: 'Report a technical problem with the platform',
  other: 'Report another issue',
};

/**
 * Local state keys for report persistence.
 * Uses sessionStorage for ephemeral state.
 */
export const REPORT_KEYS = {
  SUBMITTED: 'indeep_report_submitted',
  SUBMITTED_AT: 'indeep_report_submitted_at',
} as const;

/**
 * Description max length per safety doctrine.
 */
export const DESCRIPTION_MAX_LENGTH = 500;

/**
 * Card ID max length (validation).
 */
export const CARD_ID_MAX_LENGTH = 50;

/**
 * Submit report (local placeholder).
 * In future ACTs, this will call real API.
 */
export async function submitReport(
  draft: ReportDraft
): Promise<ReportResponse> {
  // TODO(ACT 8): Replace with: const response = await fetch('/api/reports', {...})
  // TODO(ACT 8): Emit report_submitted audit event

  // Validate draft
  if (!draft.reportType) {
    throw new Error('Report type is required');
  }
  if (!draft.acknowledged) {
    throw new Error('Acknowledgement is required');
  }
  if (draft.description && draft.description.length > DESCRIPTION_MAX_LENGTH) {
    throw new Error('Description exceeds maximum length');
  }
  if (draft.relatedCardId && draft.relatedCardId.length > CARD_ID_MAX_LENGTH) {
    throw new Error('Card ID exceeds maximum length');
  }

  // Generate placeholder report ID
  const reportId = `rpt_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  // Store locally as placeholder
  sessionStorage.setItem(REPORT_KEYS.SUBMITTED, reportId);
  sessionStorage.setItem(REPORT_KEYS.SUBMITTED_AT, new Date().toISOString());

  return {
    reportId,
    status: 'received',
    submittedAt: new Date().toISOString(),
  };
}

/**
 * Check if report was already submitted this session.
 */
export async function checkReportExists(): Promise<boolean> {
  const submitted = sessionStorage.getItem(REPORT_KEYS.SUBMITTED);
  return !!submitted;
}